if (!customElements.get('flexible-link-navigation')) {
  customElements.define('flexible-link-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.hasAttribute('data-static')) return;
      this.list = this.querySelector('.fln__list');
      if (!this.list) return;
      this.controller = new AbortController();
      this.items = [...this.list.children];
      this.arrows = this.querySelector('.fln__arrows');
      this.prev = this.querySelector('.fln__prev');
      this.next = this.querySelector('.fln__next');
      this.dots = this.querySelector('.fln__dots');
      this.more = this.querySelector('.fln__more');
      this.triggers = [...this.querySelectorAll('.fln__trigger')];
      this.panels = [...this.querySelectorAll('.fln__panel')];
      this.panelRow = this.querySelector('.fln__panels');
      for (const trigger of this.triggers) trigger.disabled = false;
      const opts = {signal:this.controller.signal};
      this.addEventListener('click', e => {
        const trigger = e.target.closest('.fln__trigger');
        if (trigger && this.triggers.includes(trigger)) this.openGroup(trigger);
      }, opts);
      this.addEventListener('keydown', e => {
        if (e.key !== 'Escape') return;
        const panel = e.target.closest('.fln__panel');
        const trigger = e.target.closest('.fln__trigger');
        const current = panel || (trigger && this.panels.find(p => p.id === trigger.getAttribute('aria-controls')));
        if (current && !current.hidden) {
          e.preventDefault();
          const owner = this.activeTrigger?.getAttribute('aria-controls') === current.id ? this.activeTrigger : this.triggers.find(t => t.getAttribute('aria-controls') === current.id);
          this.setGroup(current, false);
          owner?.focus({preventScroll:true});
        }
      }, opts);
      this.prev.addEventListener('click', () => this.move(-1), opts);
      this.next.addEventListener('click', () => this.move(1), opts);
      this.more.addEventListener('click', () => this.toggle(), opts);
      this.dots.addEventListener('click', e => {
        const button = e.target.closest('[data-page]');
        if (button) this.go(this.targets[Number(button.dataset.page)]);
      }, opts);
      this.list.addEventListener('scroll', () => this.schedule(), {...opts,passive:true});
      this.list.addEventListener('keydown', e => {
        if (e.target !== this.list || this.mode !== 'carousel') return;
        if (['ArrowLeft','ArrowRight','Home','End'].includes(e.key)) {
          e.preventDefault();
          if (e.key === 'Home') this.go(0);
          else if (e.key === 'End') this.go(this.max);
          else this.move((e.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
        }
      }, opts);
      this.addEventListener('shopify:block:select', e => {
        let item = this.querySelector(`[data-item="${CSS.escape(e.detail.blockId)}"]`);
        if (!item) return;
        const panel = item.closest('.fln__panel');
        if (panel) {
          const trigger = this.triggers.find(t => t.getAttribute('aria-controls') === panel.id);
          if (!trigger) return;
          this.openGroup(trigger, true);
          item = trigger.closest('.fln__entry');
        }
        item = item.closest('.fln__entry') || item;
        if (this.mode === 'expand' && getComputedStyle(item).display === 'none') this.toggle(true);
        if (this.mode === 'carousel') this.go(this.offset(item), true);
      }, opts);
      this.observer = new ResizeObserver(() => this.schedule());
      this.observer.observe(this.list);
      for (const item of this.items) this.observer.observe(item);
      document.fonts?.ready.then(() => {if(this.isConnected) this.schedule();});
      this.update();
      this.dataset.ready = '';
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame); this.frame = null;
      delete this.dataset.ready;
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {this.frame = null; this.update();});
    }
    offset(item) {
      const a = item.getBoundingClientRect(), b = this.list.getBoundingClientRect();
      return this.rtl ? b.right - a.right + Math.abs(this.list.scrollLeft) : a.left - b.left + this.list.scrollLeft;
    }
    update() {
      const style = getComputedStyle(this);
      this.mode = style.getPropertyValue('--layout').trim();
      this.rtl = style.direction === 'rtl';
      this.max = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
      this.sizeGroups();
      const carousel = this.mode === 'carousel' && this.max > 2;
      for (const panel of this.panels) {
        if (!panel.hidden && !this.triggers.some(t => t.getAttribute('aria-controls') === panel.id && getComputedStyle(t.closest('.fln__entry')).display !== 'none')) this.setGroup(panel, false);
      }
      this.arrows.setAttribute('aria-hidden', String(!carousel));
      this.list.tabIndex = carousel ? 0 : -1;
      this.prev.disabled = !carousel || Math.abs(this.list.scrollLeft) < 2;
      this.next.disabled = !carousel || Math.abs(this.list.scrollLeft) >= this.max - 2;
      this.prev.tabIndex = this.prev.disabled ? -1 : 0;
      this.next.tabIndex = this.next.disabled ? -1 : 0;
      this.more.disabled = false;
      this.targets = [0];
      if (carousel) {
        const width = this.list.clientWidth;
        const points = this.items.map(item => this.offset(item));
        let index = 0, target = 0;
        while (target < this.max - 1) {
          const end = Math.min(this.max, target + width);
          while (index < points.length && points[index] <= end + 1) index++;
          const point = points[index - 1];
          target = end >= this.max - 1 ? this.max : point > target + 1 ? point : end;
          this.targets.push(target);
        }
      }
      if (this.dots.children.length !== (carousel ? this.targets.length : 0)) {
        const fragment = document.createDocumentFragment();
        if (carousel) this.targets.forEach((_,i) => {
          const b = document.createElement('button'); b.type = 'button';
          b.className = 'fln__dot'; b.dataset.page = i;
          b.setAttribute('aria-controls', this.list.id);
          b.setAttribute('aria-label', (this.dataset.pageLabel || '').replace('{page}',i+1).replace('{pages}',this.targets.length));
          fragment.append(b);
        });
        this.dots.replaceChildren(fragment);
      }
      const pos = Math.abs(this.list.scrollLeft);
      const active = this.targets.reduce((a,v,i) => Math.abs(v-pos) < Math.abs(this.targets[a]-pos) ? i : a, 0);
      const first = Math.max(0, Math.min(active-3,this.targets.length-7));
      [...this.dots.children].forEach((b,i) => {
        b.setAttribute('aria-current', i === active ? 'true':'false');
        b.hidden = i < first || i >= first+7;
      });
    }
    go(position, instant = false) {
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.list.scrollTo({left:(this.rtl ? -1 : 1)*Math.max(0,Math.min(this.max,position)),behavior:instant||reduced||this.dataset.motion === 'auto' ? 'instant':'smooth'});
    }
    move(direction) {
      const pos = Math.abs(this.list.scrollLeft);
      if (this.dataset.step === 'item') {
        const points = this.items.map(item => this.offset(item));
        const target = direction > 0 ? points.find(p => p > pos+3) ?? this.max : points.reverse().find(p => p < pos-3) ?? 0;
        this.go(target);
      } else {
        const target = direction > 0 ? this.targets.find(p => p > pos+3) ?? this.max : [...this.targets].reverse().find(p => p < pos-3) ?? 0;
        this.go(target);
      }
    }
    setGroup(panel, open) {
      panel.hidden = !open;
      for (const trigger of this.triggers) if (trigger.getAttribute('aria-controls') === panel.id) trigger.setAttribute('aria-expanded', String(open));
    }
    sizeGroups() {
      if (!this.panelRow || this.dataset.groups !== 'compact') return;
      const row = this.list.getBoundingClientRect();
      const boxes = this.items.filter(item => getComputedStyle(item).display !== 'none').map(item => item.getBoundingClientRect());
      if (!boxes.length) return;
      const start = Math.max(row.left, Math.min(...boxes.map(box => box.left)));
      const end = Math.min(row.right, Math.max(...boxes.map(box => box.right)));
      const overflow = this.max > 2;
      const width = overflow ? row.width : Math.max(0, end - start);
      const offset = overflow ? 0 : this.rtl ? row.right - end : start - row.left;
      // Row measurements never depend on the panels, avoiding ResizeObserver loops.
      this.panelRow.style.setProperty('--group-span', width + 'px');
      this.panelRow.style.setProperty('--group-offset', offset + 'px');
    }
    openGroup(trigger, force) {
      const panel = this.panels.find(p => p.id === trigger.getAttribute('aria-controls'));
      if (!panel) return;
      const open = force ?? panel.hidden;
      if (open && this.dataset.groups !== 'compact' && this.dataset.multiple !== 'true') for (const other of this.panels) if (other !== panel) this.setGroup(other, false);
      this.activeTrigger = trigger;
      this.setGroup(panel, open);
    }
    toggle(force) {
      const expanded = force ?? !this.hasAttribute('data-expanded');
      const before = this.more.getBoundingClientRect().top;
      this.toggleAttribute('data-expanded', expanded);
      this.more.setAttribute('aria-expanded', String(expanded));
      if (!expanded) {
        const after = this.more.getBoundingClientRect().top;
        if (before > 0 && after < 0) {
          window.scrollBy({top:after-before,left:0,behavior:'instant'});
        }
      }
      this.schedule();
    }
  });
}
